import Tooltip from '~/components/Tooltip';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useFullRoutes } from '~/hooks';
import { Button, Icon } from '@aqarmap/aqarmap-ui';
import { formatNumber } from '~/lib';
import { ValuationItemType } from '~/types/valuationTypes';

export default function PortfolioFooter({
  id,
  yearlyChangePercentage,
  unrealizedGainPercentage,
  unrealizedGain,
}: Pick<
  ValuationItemType,
  | 'id'
  | 'yearlyChangePercentage'
  | 'unrealizedGainPercentage'
  | 'unrealizedGain'
>) {
  const { t, i18n } = useTranslation('app');
  const locale = i18n.language;
  const { VALUATION_URL } = useFullRoutes();
  const navigate = useNavigate();
  const isYearlyChangePercentagePositive = yearlyChangePercentage > 0;
  const isUnrealizedGainPercentagePositive = unrealizedGainPercentage > 0;
  const unrealizedGainClassName = isUnrealizedGainPercentagePositive
    ? 'text-green_light bg-[rgba(12,181,72,0.08)] border-[rgba(12,181,72,0.2)]'
    : 'text-red_dark bg-[rgba(229,12,12,0.08)] border-[rgba(229,12,12,0.2)]';

  const formatCappedPercentage = (
    value: number,
    maxIntegerDigits = 2,
    fractionDigits = 2,
  ) => {
    const sign = value > 0 ? '+' : '';
    const absValue = Math.abs(value);
    const integerLength = Math.floor(absValue).toString().length;

    const scaledValue =
      integerLength > maxIntegerDigits
        ? absValue / 10 ** (integerLength - maxIntegerDigits)
        : absValue;

    return `${sign}${scaledValue.toFixed(fractionDigits)}%`;
  };
  return (
    <div className="flex flex-col md:flex-row w-full h-full justify-between gap-2x pt-3x border-t border-gray">
      <div className="flex   gap-3x md:gap-7x">
        <div className="flex flex-col gap-1.5x">
          <div className="flex gap-x items-center">
            <p className="text-body_2 text-gray__dark_2 text-nowrap">
              {t('YOY')}
            </p>
            {locale === 'en' && (
              <Tooltip id="YOY-tooltip" text={t('YOYTooltipText')}>
                <Icon name="info" size="16px" className="text-blue_dark_1" />
              </Tooltip>
            )}
          </div>
          <div
            className={`flex gap-0.5x text-link ${isYearlyChangePercentagePositive ? 'text-green_light' : 'text-red_dark'}`}
          >
            <Icon
              name={
                isYearlyChangePercentagePositive ? 'up-trend' : 'down-trend'
              }
              size="24px"
            />
            <span dir={yearlyChangePercentage >= 0 ? 'rtl' : 'ltr'}>
              {yearlyChangePercentage ? `${yearlyChangePercentage}%` : '---'}
            </span>
          </div>
        </div>
        <div className="flex flex-col gap-1.5x ">
          <div className="flex gap-x items-center">
            <p className="text-body_2 text-gray__dark_2 text-nowrap">
              <Trans
                i18nKey="ROI"
                t={t}
                components={{
                  tag: <span className="text-link">placeholder</span>,
                }}
              />
            </p>
            {locale === 'en' && (
              <Tooltip id="ROI-tooltip" text={t('ROItooltipText')}>
                <Icon name="info" size="16px" className="text-blue_dark_1" />
              </Tooltip>
            )}
          </div>
          <div className="flex justify-between md:justify-start gap-1.5x ">
            <p className="text-link text-nowrap">
              {unrealizedGain
                ? `${formatNumber(unrealizedGain)} ${t('egp')}`
                : '---'}
            </p>
            <div
              className={`flex w-full justify-center align-center h-fit rounded-[8px] px-1.5x border ${unrealizedGainClassName}`}
            >
              <p className="text-caption_2 ">
                {formatCappedPercentage(unrealizedGainPercentage)}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center">
        <Button
          variant="secondary"
          size="medium"
          onClick={() => navigate(VALUATION_URL.RESULTS(id))}
          className="w-full md:w-auto"
        >
          {t('moreDetails')}
        </Button>
      </div>
    </div>
  );
}
